import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";

const REGIONS = ["ghana", "africa", "diaspora", "united_states", "global"];

export default function Projects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", region: "ghana", description: "" });

  const load = () => api.get("/projects").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      setForm({ title: "", region: "ghana", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6">
      <PageHeader title="Research Projects" subtitle="Investigations and monitoring workspaces.">
        <Btn data-testid="new-project-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Project</Btn>
      </PageHeader>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="projects-list">
        {items.length === 0 && <p className="text-sm text-zinc-600">No projects yet. Create one to begin.</p>}
        {items.map((p) => (
          <Card key={p.id} className="p-4 hover:border-zinc-700 transition-colors">
            <div className="flex items-center justify-between">
              <StatusBadge status={p.status} />
              <span className="label-caps text-zinc-600">{p.region}</span>
            </div>
            <h3 className="mt-3 font-tactical font-bold text-lg leading-tight">{p.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-500 line-clamp-2">{p.description}</p>
            <Link
              to={`/command-center/projects/${p.id}`}
              data-testid={`open-project-${p.id}`}
              className="mt-4 inline-flex items-center gap-1 text-sm text-emerald-400 hover:gap-2 transition-all"
            >
              Open <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Research Project">
        <form onSubmit={create} className="space-y-4">
          <Field label="Title">
            <input data-testid="project-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Region">
            <select className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Btn data-testid="project-save-btn" type="submit" className="w-full justify-center">Create Project</Btn>
        </form>
      </Modal>
    </div>
  );
}
